import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LocationDto, PrinterDto, UploadLocationsResult, WarehouseDto } from '@thebb/shared';
import { get, patch, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner } from '../components/ui';

export const locationTypeLabel: Record<string, string> = { picking: 'Pluk', bulk: 'Bulk', receiving: 'Modtagelse', staging: 'Klargøring', pallet: 'Palleplads', quarantine: 'Karantæne' };
const types = Object.keys(locationTypeLabel);

/** Locations per warehouse: list, create, bulk upload and label printing. */
export function LocationsPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [upload, setUpload] = useState<{ text: string; defaultType: string } | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadLocationsResult | null>(null);
  const [printerId, setPrinterId] = useState('');
  const [newCode, setNewCode] = useState('');

  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  useEffect(() => {
    if (!warehouseId && warehouses.data?.length) setWarehouseId(warehouses.data[0]!.id);
  }, [warehouses.data, warehouseId]);
  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (warehouseId) p.set('warehouseId', warehouseId);
    if (type) p.set('type', type);
    if (q) p.set('q', q);
    if (includeInactive) p.set('includeInactive', 'true');
    p.set('perPage', '500');
    return p.toString();
  }, [warehouseId, type, q, includeInactive]);
  const list = useQuery({ queryKey: ['locations', params], queryFn: () => get<{ items: LocationDto[]; total: number }>(`/locations?${params}`), enabled: !!warehouseId });
  const printers = useQuery({ queryKey: ['printers'], queryFn: () => get<PrinterDto[]>('/printing/printers'), enabled: can('stock.locations') });
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['locations'] }); void qc.invalidateQueries({ queryKey: ['warehouses'] }); };

  const create = useMutation({ mutationFn: () => post('/locations', { warehouseId, code: newCode, type: type || 'picking' }), onSuccess: () => { setNewCode(''); invalidate(); }, onError: setError });
  const doUpload = useMutation({
    mutationFn: (u: { text: string; defaultType: string }) => post<UploadLocationsResult>('/locations/upload', { warehouseId, text: u.text, defaultType: u.defaultType }),
    onSuccess: (r) => { setUploadResult(r); setUpload(null); invalidate(); },
    onError: setError,
  });
  const update = useMutation({ mutationFn: (v: { id: string; patch: Record<string, unknown> }) => patch(`/locations/${v.id}`, v.patch), onSuccess: invalidate, onError: setError });
  const print = useMutation({
    mutationFn: () => post<{ labels: number; format: string }>('/locations/print-labels', { printerId, locationIds: [...selected] }),
    onSuccess: () => setSelected(new Set()),
    onError: setError,
  });

  const items = list.data?.items ?? [];
  const allSelected = items.length > 0 && items.every((l) => selected.has(l.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((l) => l.id)));
  const toggleOne = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div>
      <PageTitle
        title="Lokationer"
        actions={
          <>
            <select className="input w-40" value={warehouseId} onChange={(e) => { setWarehouseId(e.target.value); setSelected(new Set()); }}>
              {warehouses.data?.map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
            </select>
            <select className="input w-36" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Alle typer</option>
              {types.map((t) => <option key={t} value={t}>{locationTypeLabel[t]}</option>)}
            </select>
            <input className="input w-36" placeholder="Søg kode" value={q} onChange={(e) => setQ(e.target.value)} />
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} /> Vis lukkede</label>
            {can('stock.locations') && <button className="btn" onClick={() => setUpload({ text: '', defaultType: 'picking' })}>Upload liste</button>}
          </>
        }
      />
      <ErrorBox error={error} />
      {uploadResult && (
        <div className="mb-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          Upload: {uploadResult.created} nye, {uploadResult.updated} opdateret, {uploadResult.rejected.length} afvist.
          {uploadResult.rejected.length > 0 && <ul className="mt-1 list-disc pl-5 text-red-700">{uploadResult.rejected.slice(0, 20).map((r) => <li key={r.line}>linje {r.line}: {r.text} ({r.reason})</li>)}</ul>}
        </div>
      )}
      {print.data && <div className="mb-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{print.data.labels} labels sendt til print ({print.data.format}).</div>}

      {can('stock.locations') && (
        <div className="card mb-4 flex flex-wrap items-end gap-3 text-sm">
          <div>
            <label className="label">Ny lokation</label>
            <div className="flex gap-2">
              <input className="input w-32" placeholder="C023" value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && newCode && create.mutate()} />
              <button className="btn" disabled={!newCode || !warehouseId || create.isPending} onClick={() => create.mutate()}>Opret</button>
            </div>
          </div>
          <div className="ml-auto">
            <label className="label">Print labels ({selected.size} valgt)</label>
            <div className="flex gap-2">
              <select className="input w-56" value={printerId} onChange={(e) => setPrinterId(e.target.value)}>
                <option value="">Vælg printer</option>
                {printers.data?.filter((p) => p.enabled).map((p) => <option key={p.id} value={p.id}>{p.displayName} ({p.kind === 'raw' ? 'ZPL' : 'PDF'})</option>)}
              </select>
              <button className="btn btn-primary" disabled={!printerId || selected.size === 0 || print.isPending} onClick={() => print.mutate()}>{print.isPending ? 'Sender…' : 'Print'}</button>
            </div>
          </div>
        </div>
      )}

      {list.isLoading ? (
        <Spinner />
      ) : (
        <table className="table card p-0">
          <thead>
            <tr>
              <th><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              <th>Kode</th>
              <th>Række</th>
              <th>Sektion</th>
              <th>Etage</th>
              <th>Type</th>
              <th>Stregkode</th>
              <th className="text-right">Varer</th>
              <th className="text-right">Antal</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} className={!l.active ? 'text-zinc-400' : ''}>
                <td><input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleOne(l.id)} /></td>
                <td className="font-medium">{l.code}</td>
                <td>{l.row ?? '–'}</td>
                <td>{l.section ?? '–'}</td>
                <td>{l.floor ?? '–'}</td>
                <td>
                  {can('stock.locations') ? (
                    <select className="input py-0.5" value={l.type} onChange={(e) => update.mutate({ id: l.id, patch: { type: e.target.value } })}>
                      {types.map((t) => <option key={t} value={t}>{locationTypeLabel[t]}</option>)}
                    </select>
                  ) : (
                    locationTypeLabel[l.type]
                  )}
                </td>
                <td className="font-mono text-xs">{l.barcode}</td>
                <td className="text-right tabular-nums">{l.variantCount}</td>
                <td className="text-right tabular-nums">{l.onHand}</td>
                <td>
                  {l.active ? <Badge tone="green">aktiv</Badge> : <Badge>lukket</Badge>}
                  {can('stock.locations') && <button className="ml-2 text-xs text-blue-700 hover:underline" onClick={() => update.mutate({ id: l.id, patch: { active: !l.active } })}>{l.active ? 'luk' : 'åbn'}</button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={10} className="text-zinc-500">Ingen lokationer. Opret enkeltvis eller upload en liste.</td></tr>}
          </tbody>
        </table>
      )}
      {list.data && list.data.total > items.length && <div className="mt-2 text-xs text-zinc-500">Viser {items.length} af {list.data.total}. Indsnævr med søgning.</div>}

      {upload && (
        <Modal title="Upload lokationer" onClose={() => setUpload(null)}>
          <p className="mb-2 text-sm text-zinc-600">Én lokation pr. linje: <code>kode;type;stregkode</code>. Type og stregkode kan udelades. Eksisterende koder opdateres.</p>
          <div className="mb-2">
            <label className="label">Standardtype</label>
            <select className="input w-48" value={upload.defaultType} onChange={(e) => setUpload({ ...upload, defaultType: e.target.value })}>
              {types.map((t) => <option key={t} value={t}>{locationTypeLabel[t]}</option>)}
            </select>
          </div>
          <textarea className="input h-64 font-mono text-xs" value={upload.text} onChange={(e) => setUpload({ ...upload, text: e.target.value })} placeholder={'A011\nA012\nB031;bulk\nMODT-1;receiving'} />
          <div className="mt-4 flex justify-end gap-2">
            <button className="btn" onClick={() => setUpload(null)}>Annullér</button>
            <button className="btn btn-primary" disabled={!upload.text.trim() || doUpload.isPending} onClick={() => doUpload.mutate(upload)}>{doUpload.isPending ? 'Uploader…' : 'Upload'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
